import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { cn } from "../../utils/cn";
import { Field } from "./Field";

export interface FileUploadProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  value?: File[];
  onChange?: (files: File[]) => void;
  disabled?: boolean;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function FileUpload({
  label,
  helper,
  error,
  required,
  accept,
  multiple = true,
  maxSizeMB = 10,
  value = [],
  onChange,
  disabled,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    const valid = incoming.filter((f) => f.size <= maxSizeMB * 1024 * 1024);
    onChange?.(multiple ? [...value, ...valid] : valid);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  return (
    <Field label={label} helper={helper} error={error} required={required}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-6 text-center transition-colors",
          dragOver ? "border-primary-400 bg-primary-50" : "border-background-300 hover:border-background-400",
          disabled && "cursor-not-allowed opacity-60",
          error && "border-danger-300"
        )}
      >
        <i className="ri-upload-cloud-2-line text-2xl text-foreground-400" />
        <p className="text-sm text-foreground-700">
          Drag &amp; drop files here, or <span className="font-medium text-primary-600">browse</span>
        </p>
        <p className="text-xs text-foreground-500">
          {accept ? `Accepts ${accept} · ` : ""}Max {maxSizeMB} MB each
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          className="hidden"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {value.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {value.map((file, i) => (
            <li key={`${file.name}-${i}`} className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-1.5 text-sm">
              <i className="ri-file-line text-base text-foreground-400" />
              <span className="min-w-0 flex-1 truncate text-foreground-800">{file.name}</span>
              <span className="tnum flex-none text-xs text-foreground-500">{formatSize(file.size)}</span>
              <button type="button" onClick={() => removeFile(i)} aria-label="Remove file" className="flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded text-foreground-400 hover:text-danger-600">
                <i className="ri-close-line text-sm" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Field>
  );
}